/**
 * Theme controller. Dark mode is class-driven: `.dark` on <html> (see
 * globals.css `@custom-variant dark`). "system" tracks the OS preference
 * live via a matchMedia change listener; an explicit choice detaches it.
 */

export type Theme = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "erp.theme";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

/** Read the persisted theme; anything missing/unknown falls back to "system". */
export function getStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    // Storage can be unavailable (privacy mode, sandboxed iframe).
    return "system";
  }
}

let systemMql: MediaQueryList | null = null;
let systemListener: ((event: MediaQueryListEvent) => void) | null = null;

function setDarkClass(dark: boolean): void {
  document.documentElement.classList.toggle("dark", dark);
}

function detachSystemListener(): void {
  if (systemMql && systemListener) {
    systemMql.removeEventListener("change", systemListener);
  }
  systemMql = null;
  systemListener = null;
}

/**
 * Apply a theme to <html>. "system" resolves against
 * `prefers-color-scheme: dark` and keeps following OS changes until an
 * explicit theme is applied.
 */
export function applyTheme(theme: Theme): void {
  detachSystemListener();
  if (theme === "system") {
    systemMql = window.matchMedia("(prefers-color-scheme: dark)");
    setDarkClass(systemMql.matches);
    systemListener = (event) => setDarkClass(event.matches);
    systemMql.addEventListener("change", systemListener);
    return;
  }
  setDarkClass(theme === "dark");
}

/** Persist and apply. All user-driven theme changes go through here. */
export function setTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Non-fatal: theme still applies for this page lifetime.
  }
  applyTheme(theme);
}

/** Boot-time hook: apply whatever the user chose last (default "system"). */
export function initTheme(): void {
  applyTheme(getStoredTheme());
}
